// 데이터 층. Firebase 설정이 있으면 Firestore 실시간 구독, 없으면 번들된 예시 데이터(assets/data/sample_deals.json)로 같은 API 를 흉내 낸다.
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signInAnonymously } from 'firebase/auth';
import {
  addDoc, collection, deleteDoc, doc, Firestore, initializeFirestore, limit, onSnapshot, orderBy, query, serverTimestamp,
  setDoc, updateDoc, where, type DocumentData, type QueryDocumentSnapshot, type DocumentSnapshot,
} from 'firebase/firestore';
import sample from '../assets/data/sample_deals.json';
import { firebaseConfig } from './firebaseConfig';
import type { AlertDoc, Condition, Deal, Label, Summary } from './types';

export const hasFirebase = Boolean(firebaseConfig.projectId);

let db: Firestore | null = null;
let authReady: Promise<void> = Promise.resolve();
// 내가 만든 문서에 주인을 적는다. 보안 규칙이 이걸로 남의 조건 수정을 막고,
// 수집기가 이걸로 사용자별 AI 한도를 센다.
let uid: string | null = null;
if (hasFirebase) {
  const app = initializeApp(firebaseConfig);
  db = initializeFirestore(app, { experimentalForceLongPolling: Platform.OS !== 'web' });
  const auth = getAuth(app);
  authReady = new Promise((resolve) =>
    onAuthStateChanged(auth, (u) => {
      // 카카오로 갈아타면 uid 가 바뀐다. 마지막 값을 계속 들고 있어야 한다
      uid = u ? u.uid : null;
      if (u) resolve();
    }),
  );
  signInAnonymously(auth).catch((e) => console.warn('익명 로그인 실패', e));
}

// ---------------------------------------------------------------- 예시 모드 저장소
type State = { deals: Deal[]; alerts: AlertDoc[]; conditions: Condition[]; labels: Record<string, Label>; summary: Summary };
const sampleDeals = (sample.deals as unknown as Deal[]).slice().sort((a, b) => b.score - a.score);
const state: State = {
  deals: sampleDeals,
  alerts: [],
  conditions: [
    { id: 'demo-1', text: '호그와트 레거시 1만원 이하', active: true, created_at: new Date(), parsed: '키워드(모두): 호그와트, 레거시 | 가격 10,000원 이하 | 60점 이상', parsed_by: 'rules' },
    { id: 'demo-2', text: '990 PRO 1TB 15만원 이하 중고 제외', active: false, created_at: new Date(), parsed: '키워드(모두): 990, PRO, 1TB | 가격 150,000원 이하 | 제외: 중고 | 60점 이상', parsed_by: 'ai:deepseek' },
  ],
  labels: {},
  summary: sample.summary as Summary,
};
{
  const hit = sampleDeals.find((d) => d.product.includes('호그와트'));
  if (hit) {
    state.alerts.push({
      id: 'demo-alert', condition_id: 'demo-1', condition_text: '호그와트 레거시 1만원 이하', deal_id: hit.id, key: hit.key, product: hit.product,
      mall: hit.mall, effective_price: hit.effective_price, score: hit.score, confidence: hit.confidence, reasons: hit.reasons, url: hit.urls[0] ?? null,
      product_url: hit.product_url, message: '', read: false, created_at: new Date(),
    });
  }
}
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
function subscribeSample(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ---------------------------------------------------------------- 변환
const toDate = (v: unknown): Date | null => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const t = v as { toDate?: () => Date };
  if (typeof t.toDate === 'function') return t.toDate();
  const d = new Date(String(v));
  return Number.isNaN(d.getTime()) ? null : d;
};
const dealOf = (s: QueryDocumentSnapshot<DocumentData> | DocumentSnapshot<DocumentData>): Deal => ({ ...(s.data() as Deal), id: s.id });
const alertOf = (s: QueryDocumentSnapshot<DocumentData>): AlertDoc => {
  const x = s.data();
  return { ...(x as AlertDoc), id: s.id, created_at: toDate(x.created_at), reasons: x.reasons ?? [] };
};
const conditionOf = (s: QueryDocumentSnapshot<DocumentData>): Condition => {
  const x = s.data();
  return { id: s.id, text: x.text ?? '', active: x.active !== false, created_at: toDate(x.created_at), parsed: x.parsed, spec: x.spec,
    parsed_by: x.parsed_by, local_id: x.local_id, uid: x.uid, needs_confirm: x.needs_confirm === true, question: x.question,
    candidates: Array.isArray(x.candidates) ? x.candidates : undefined, keywords_override: x.keywords_override,
    chat: Array.isArray(x.chat) ? x.chat : undefined };
};

// ---------------------------------------------------------------- 읽기 훅
export function useDeals(): Deal[] {
  const [deals, setDeals] = useState<Deal[]>(db ? [] : state.deals);
  useEffect(() => {
    if (!db) return subscribeSample(() => setDeals(state.deals));
    const d = db;
    let unsub = () => {};
    authReady.then(() => {
      unsub = onSnapshot(query(collection(d, 'deals'), where('active', '==', true), limit(200)), (snap) =>
        setDeals(snap.docs.map(dealOf).sort((a, b) => b.score - a.score)),
      );
    });
    return () => unsub();
  }, []);
  return deals;
}

export function useDeal(id: string | undefined): Deal | null | undefined {
  // undefined = 아직 모름, null = 없음
  const [deal, setDeal] = useState<Deal | null | undefined>(db ? undefined : state.deals.find((x) => x.id === id) ?? null);
  useEffect(() => {
    if (!id) return;
    if (!db) return subscribeSample(() => setDeal(state.deals.find((x) => x.id === id) ?? null));
    const d = db;
    let unsub = () => {};
    authReady.then(() => {
      unsub = onSnapshot(doc(d, 'deals', id), (snap) => setDeal(snap.exists() ? dealOf(snap) : null));
    });
    return () => unsub();
  }, [id]);
  return deal;
}

export function useAlerts(): AlertDoc[] {
  const [alerts, setAlerts] = useState<AlertDoc[]>(db ? [] : state.alerts);
  useEffect(() => {
    if (!db) return subscribeSample(() => setAlerts([...state.alerts]));
    const d = db;
    let unsub = () => {};
    authReady.then(() => {
      unsub = onSnapshot(query(collection(d, 'alerts'), orderBy('created_at', 'desc'), limit(100)), (snap) => setAlerts(snap.docs.map(alertOf)));
    });
    return () => unsub();
  }, []);
  return alerts;
}

export function useConditions(): Condition[] {
  const [conds, setConds] = useState<Condition[]>(db ? [] : state.conditions);
  const me = useAccount().uid; // 로그인으로 uid 가 바뀌면 구독을 다시 건다
  useEffect(() => {
    if (!db) return subscribeSample(() => setConds([...state.conditions]));
    const d = db;
    let unsub = () => {};
    authReady.then(() => {
      // 서버에서부터 내 것만 받아온다. 앱에서 거르면 남의 조건이 기기까지는 내려온다.
      if (!uid) return;
      unsub = onSnapshot(
        query(collection(d, 'conditions'), where('uid', '==', uid), orderBy('created_at', 'desc')),
        (snap) => setConds(snap.docs.map(conditionOf)),
      );
    });
    return () => unsub();
  }, [me]);
  return conds;
}

export function useSummary(): Summary | null {
  const [s, setS] = useState<Summary | null>(db ? null : state.summary);
  useEffect(() => {
    if (!db) return;
    const d = db;
    let unsub = () => {};
    authReady.then(() => {
      unsub = onSnapshot(doc(d, 'meta', 'summary'), (snap) => setS(snap.exists() ? (snap.data() as Summary) : null));
    });
    return () => unsub();
  }, []);
  return s;
}

export function useLabel(dealId: string | undefined): Label | undefined {
  const [label, setLabel] = useState<Label | undefined>(db || !dealId ? undefined : state.labels[dealId]);
  useEffect(() => {
    if (!dealId) return;
    if (!db) return subscribeSample(() => setLabel(state.labels[dealId]));
    const d = db;
    let unsub = () => {};
    authReady.then(() => {
      unsub = onSnapshot(doc(d, 'labels', dealId), (snap) => setLabel(snap.exists() ? (snap.data().label as Label) : undefined));
    });
    return () => unsub();
  }, [dealId]);
  return label;
}

// ---------------------------------------------------------------- 쓰기
/** 만든 조건의 id 를 돌려준다. 화면이 그 조건을 구독해 AI 해석·되물음을 바로 보여 줄 수 있어야 한다. */
export async function addCondition(text: string): Promise<string> {
  if (!db) {
    const id = `local-${Date.now()}`;
    state.conditions = [{ id, text, active: true, created_at: new Date() }, ...state.conditions];
    emit();
    return id;
  }
  await authReady;
  const ref = await addDoc(collection(db, 'conditions'), { text, active: true, uid, created_at: serverTimestamp() });
  return ref.id;
}

export async function setConditionActive(id: string, active: boolean): Promise<void> {
  if (!db) {
    state.conditions = state.conditions.map((c) => (c.id === id ? { ...c, active } : c));
    emit();
    return;
  }
  await updateDoc(doc(db, 'conditions', id), { active });
}

/** 사용자가 고른 제품 목록. 수집기가 다음 사이클에 조건에 반영하고 needs_confirm 을 내린다. */
export async function setConditionKeywords(id: string, keywords: string[]): Promise<void> {
  if (!db) {
    state.conditions = state.conditions.map((c) => (c.id === id ? { ...c, keywords_override: keywords, needs_confirm: false } : c));
    emit();
    return;
  }
  await updateDoc(doc(db, 'conditions', id), { keywords_override: keywords });
}

/** 되묻기 대화에 사용자 답을 붙인다. 수집기가 리스너로 지켜보다 바로 답한다. */
export async function replyToCondition(id: string, text: string, chat: Condition['chat']): Promise<void> {
  const next = [...(chat ?? []), { role: 'user' as const, text }];
  if (!db) {
    state.conditions = state.conditions.map((c) => (c.id === id ? { ...c, chat: next } : c));
    emit();
    return;
  }
  await updateDoc(doc(db, 'conditions', id), { chat: next });
}

/**
 * 지금 계정 상태. 커스텀 토큰으로 만든 카카오 계정은 providerData 가 비어 있어서
 * 그걸로는 익명과 구분되지 않는다. uid 접두어로 본다 (함수가 kakao:{회원번호} 로 만든다).
 * 진짜 판단은 서버가 커스텀 클레임으로 하고, 여기서는 화면 표시용으로만 쓴다.
 */
export function useAccount(): { uid: string | null; signedUp: boolean; nickname: string } {
  const [a, setA] = useState<{ uid: string | null; signedUp: boolean; nickname: string }>({
    uid: null, signedUp: false, nickname: '',
  });
  useEffect(() => {
    if (!hasFirebase) return;
    const auth = getAuth();
    return onAuthStateChanged(auth, (u) =>
      setA({ uid: u?.uid ?? null, signedUp: !!u && u.uid.startsWith('kakao:'), nickname: u?.displayName ?? '' }),
    );
  }, []);
  return a;
}

/** 지금 로그인된 uid. 카카오로 갈아탈 때 익명 uid 를 넘겨 주기 위해 쓴다. */
export function currentUid(): string | null {
  return uid;
}

/**
 * 앱이 열릴 때 출석 도장을 찍는다. 크레딧은 수집기가 서버 날짜로 판단해 준다.
 * fromUid 를 주면 그 익명 계정에 쌓인 크레딧·조건을 지금 계정으로 옮긴다.
 */
export async function checkIn(fromUid?: string): Promise<void> {
  if (!db) return;
  await authReady;
  if (!uid) return;
  const day = new Date().toISOString().slice(0, 10);
  const body: Record<string, unknown> = { uid, at: serverTimestamp() };
  if (fromUid && fromUid !== uid) body.from_uid = fromUid;
  await setDoc(doc(db, 'checkins', `${uid}_${day}`), body, { merge: true }).catch(() => {});
}

/** 내 크레딧 잔액. 수집기만 쓰고 앱은 읽기만 한다. */
export function useCredits(): number | null {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    if (!db) return;
    let off = () => {};
    authReady.then(() => {
      if (!db || !uid) return;
      off = onSnapshot(doc(db, 'users', uid), (s) => setN(Number((s.data() as { credits?: number } | undefined)?.credits ?? 0)));
    });
    return () => off();
  }, []);
  return n;
}

export async function removeCondition(id: string): Promise<void> {
  if (!db) {
    state.conditions = state.conditions.filter((c) => c.id !== id);
    emit();
    return;
  }
  await deleteDoc(doc(db, 'conditions', id));
}

export async function setLabel(deal: Deal, label: Label): Promise<void> {
  if (!db) {
    state.labels = { ...state.labels, [deal.id]: label };
    emit();
    return;
  }
  await setDoc(doc(db, 'labels', deal.id), { key: deal.key, label, synced: false, created_at: serverTimestamp() });
}

export async function markAlertRead(id: string): Promise<void> {
  if (!db) {
    state.alerts = state.alerts.map((a) => (a.id === id ? { ...a, read: true } : a));
    emit();
    return;
  }
  await updateDoc(doc(db, 'alerts', id), { read: true });
}

export async function registerDevice(token: string, platform: string): Promise<void> {
  if (!db) return;
  await authReady;
  lastDeviceToken = token;
  await setDoc(doc(db, 'devices', token), { platform, updated_at: serverTimestamp() });
}

// 이 기기가 등록한 푸시 토큰. 계정을 지울 때 같이 떼려고 들고 있는다
let lastDeviceToken: string | null = null;

/** 이 기기로는 더 이상 알림을 보내지 않게 한다. 계정 삭제 때 부른다. */
export async function forgetThisDevice(): Promise<void> {
  if (!db || !lastDeviceToken) return;
  await deleteDoc(doc(db, 'devices', lastDeviceToken));
  lastDeviceToken = null;
}
